import React, { useState, useRef, useEffect } from 'react';
import { analyzeSymptoms } from '../services/aiService';
import {
    MessageSquare, X, Send, Bot, Stethoscope, FileText,
    AlertTriangle, ShieldAlert, Activity, StopCircle,
    Volume2, VolumeX, Moon, Sun, LayoutGrid, User,
    Trash2, Sparkles, CheckCircle, Copy, ThumbsUp,
    ThumbsDown, MapPin, Calendar, Smartphone,
    Mic, MicOff, MessageCircle, Info, Accessibility
} from 'lucide-react';
import { db, auth } from '../firebase'; // Import auth
import { useAuthState } from 'react-firebase-hooks/auth'; // Import hook
import { collection, query, where, getDocs, addDoc, doc, getDoc } from 'firebase/firestore'; // Import doc, getDoc
import BookingModal from './BookingModal';
import { generateChatReport } from '../utils/reportGenerator';
import BodyMap from './BodyMap';
import MedicalCard from './MedicalCard';
import UserProfileModal from './UserProfileModal'; // NEW: Import Profile Modal
import VoiceWaveform from './VoiceWaveform';
import DashboardModal from './DashboardModal'; // NEW: Import Dashboard
import { triggerConfetti } from '../utils/confetti';
import HealthStats from './HealthStats'; // Import Stats
import './ChatBot.css';

// [Cleaned up misplaced code]

const TRANSLATIONS = {
    en: {
        greeting: "Hello! I am PulseNote AI. Describe your symptoms, and I will recommend a specialist.",
        placeholder: "Describe symptoms...",
        listening: "Listening...",
        speak: "Speak",
        send: "Send",
        report: "Download Report",
        sos: "Emergency SOS",
        sosMsg: "EMERGENCY MODE ACTIVATED",
        sosSub: "Please contact emergency services immediately:",
        recTitle: "Recommended Specialists:",
        bookBtn: "Book",
        switchLang: "Switch Language",
        bookSuccess: "Appointment booked with",
        bookFail: "Failed to book. Please try again.",
        errAI: "Error connecting to AI.",
        errGen: "Error.",
        map: "Directions",
        clear: "Clear Chat",
        disclaimerTitle: "Medical Disclaimer",
        disclaimerText: "This AI chatbot provides general information for educational purposes only. It is NOT a substitute for professional medical advice, diagnosis, or treatment. In case of emergency, call 999 or go to the nearest hospital immediately.",
        understand: "I Understand",
        feedbackThanks: "Thanks for your feedback!",
        chips: ["Severe Headache", "High Fever", "Chest Pain", "Skin Rash", "Stomach Pain"],
        mute: "Mute Sound",
        unmute: "Unmute Sound",
        darkMode: "Dark Mode",
        lightMode: "Light Mode",
        tipTitle: "Daily Health Tip",
        alertHdr: "ALERT: EMERGENCY DETECTED",
        bodyMap: "Visual Body Map"
    },
    bn: {
        greeting: "নমস্কার! আমি পালসনোট এআই। আপনার লক্ষণগুলি বলুন, আমি সঠিক বিশেষজ্ঞের পরামর্শ দেব।",
        placeholder: "লক্ষণগুলি বর্ণনা করুন...",
        listening: "শুনছি...",
        speak: "বলুন",
        send: "পাঠান",
        report: "রিপোর্ট ডাউনলোড",
        sos: "জরুরী সেবা",
        sosMsg: "জরুরী মোড চালু করা হয়েছে",
        sosSub: "অনুগ্রহ করে অবিলম্বে যোগাযোগ করুন:",
        recTitle: "প্রস্তাবিত বিশেষজ্ঞগণ:",
        bookBtn: "অ্যাপয়েন্টমেন্ট নিন",
        switchLang: "ভাষা পরিবর্তন",
        bookSuccess: "অ্যাপয়েন্টমেন্ট নিশ্চিত হয়েছে - ",
        bookFail: "বুকিং ব্যর্থ হয়েছে। আবার চেষ্টা করুন।",
        errAI: "এআই সংযোগে সমস্যা হচ্ছে।",
        errGen: "সমস্যা হয়েছে।",
        map: "ম্যাপ দেখুন",
        clear: "চ্যাট মুছুন",
        disclaimerTitle: "সতর্কবার্তা",
        disclaimerText: "এই এআই চ্যাটবট শুধুমাত্র সাধারণ তথ্যের জন্য। এটি কোনো বিশেষজ্ঞ চিকিৎসকের বিকল্প নয়। জরুরী প্রয়োজনে ৯৯৯ এ কল করুন অথবা নিকটস্থ হাসপাতালে যান।",
        understand: "আমি বুঝতে পেরেছি",
        feedbackThanks: "মতামতের জন্য ধন্যবাদ!",
        chips: ["প্রচণ্ড মাথা ব্যথা", "উচ্চ জ্বর", "বুক ব্যথা", "চামড়ায় র‍্যাশ", "পেট ব্যথা"],
        mute: "শব্দ বন্ধ করুন",
        unmute: "শব্দ চালু করুন",
        darkMode: "ডার্ক মোড",
        lightMode: "লাইট মোড",
        tipTitle: "দৈনিক স্বাস্থ্য টিপ",
        alertHdr: "সতর্কতা: জরুরী অবস্থা",
        bodyMap: " ভিজ্যুয়াল বডি ম্যাপ"
    }
};

const HEALTH_TIPS = {
    en: [
        "Drink at least 8 glasses of water daily to stay hydrated.",
        "Take a 10-minute walk after meals to aid digestion.",
        "Ensure you get 7-8 hours of sleep for better immunity.",
        "Reduce sugar intake to maintain consistent energy levels.",
        "Practice deep breathing for 2 minutes when feeling stressed.",
        "Maintain good posture while sitting to avoid back pain."
    ],
    bn: [
        "শরীর সুস্থ রাখতে প্রতিদিন অন্তত ৮ গ্লাস পানি পান করুন।",
        "হজম শক্তি বাড়াতে খাবারের পর ১০ মিনিট হাঁটাহাঁটি করুন।",
        "রোগ প্রতিরোধ ক্ষমতা বাড়াতে দৈনিক ৭-৮ ঘণ্টা ঘুমান।",
        "অতিরিক্ত চিনি খাওয়া কমিয়ে দিন, এতে শক্তি বজায় থাকে।",
        "মানসিক চাপে থাকলে ২ মিনিট গভীর শ্বাস নিন।",
        "পিঠের ব্যথা এড়াতে বসার সময় মেরুদণ্ড সোজা রাখুন।"
    ]
};

const SPECIALTY_MAP_BN = {
    "General Physician": "সাধারণ চিকিৎসক",
    "Neurologist": "নিউরোলজিস্ট (মস্তিষ্ক বিশেষজ্ঞ)",
    "Cardiologist": "কার্ডিওলজিস্ট (হৃদরোগ বিশেষজ্ঞ)",
    "Dermatologist": "চর্মরোগ বিশেষজ্ঞ",
    "ENT Specialist": "নাক কান গলা বিশেষজ্ঞ",
    "Pediatrician": "শিশু বিশেষজ্ঞ",
    "Orthopedic": "অর্থোপেডিক (হাড় ও জোড়া)",
    "Psychiatrist": "মানসিক রোগ বিশেষজ্ঞ",
    "Dentist": "দন্ত চিকিৎসক",
    "Gynaecologist": "স্ত্রীরোগ বিশেষজ্ঞ",
    "Ophthalmologist": "চক্ষু বিশেষজ্ঞ",
    "Urologist": "ইউরোলজিস্ট (কিডনি ও মূত্রনালী)",
    "Oncologist": "ক্যানসার বিশেষজ্ঞ",
    "Gastroenterologist": "গ্যাস্ট্রোলিভার বিশেষজ্ঞ"
};

// Web Audio API Pop Sound
const playPopSound = (muted) => {
    if (muted) return;
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(800, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
        osc.start();
        osc.stop(ctx.currentTime + 0.1);
    } catch (e) { console.error("Audio error", e); }
};

// Streaming Text Component
const Typewriter = ({ text, onComplete }) => {
    const [displayedText, setDisplayedText] = useState('');
    const indexRef = useRef(0);

    useEffect(() => {
        indexRef.current = 0;
        setDisplayedText('');

        const interval = setInterval(() => {
            if (indexRef.current < text.length) {
                setDisplayedText((prev) => prev + text.charAt(indexRef.current));
                indexRef.current++;
            } else {
                clearInterval(interval);
                if (onComplete) onComplete();
            }
        }, 15);

        return () => clearInterval(interval);
    }, [text, onComplete]);

    return (
        <div
            className="typewriter-text"
            dangerouslySetInnerHTML={{ __html: displayedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}
        />
    );
};

const ChatBot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [language, setLanguage] = useState(localStorage.getItem('settings_language') || 'en');
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(''); // NEW: Thinking Phase
    const [recommendedDoctors, setRecommendedDoctors] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [isListening, setIsListening] = useState(false);
    const [sosActive, setSosActive] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isMuted, setIsMuted] = useState(localStorage.getItem('settings_muted') === 'true');
    const [theme, setTheme] = useState(localStorage.getItem('settings_theme') || 'light');
    const [audioStream, setAudioStream] = useState(null); // NEW: Audio Stream for visualizer
    const [dailyTip, setDailyTip] = useState('');
    const [isTypingDone, setIsTypingDone] = useState(true);
    const [speakingIndex, setSpeakingIndex] = useState(null);

    const [showBodyMap, setShowBodyMap] = useState(false);
    const [dynamicChips, setDynamicChips] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isDashboardOpen, setIsDashboardOpen] = useState(false); // NEW: Dashboard State

    const messagesEndRef = useRef(null);
    const t = TRANSLATIONS[language];

    // Initialize & Load
    const [user] = useAuthState(auth); // NEW: Check Auth
    const [isDoctor, setIsDoctor] = useState(false);

    useEffect(() => {
        const checkUserRoleAndHydrate = async () => {
            if (user) {
                // 1. Check if Doctor
                console.log("ChatBot: Checking role...", user.uid);
                const docRef = doc(db, "doctors", user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    console.log("ChatBot: User IS doctor. Hiding.");
                    setIsDoctor(true);
                    return;
                }

                // 2. Hydrate Patient Profile
                try {
                    const patRef = doc(db, "patients", user.uid);
                    const patSnap = await getDoc(patRef);
                    if (patSnap.exists()) {
                        const data = patSnap.data();
                        const calculatedAge = data.age || (data.dateOfBirth ? new Date().getFullYear() - new Date(data.dateOfBirth).getFullYear() : "N/A");

                        const profile = {
                            name: data.fullName || data.name || "Patient",
                            age: calculatedAge,
                            gender: data.gender || "Not Specified",
                            language: data.language || "en"
                        };

                        // Auto-save to state and storage (so modal doesn't open)
                        setUserProfile(profile);
                        if (data.language === 'bn') setLanguage('bn');
                        localStorage.setItem('user_profile', JSON.stringify(profile));
                        console.log("ChatBot: Hydrated profile:", profile);
                    }
                } catch (e) {
                    console.error("ChatBot: Profile hydration failed", e);
                }
            }
        };
        checkUserRoleAndHydrate();
    }, [user]);

    // HIDE if not logged in OR if user is a doctor
    // (MOVED TO BOTTOM TO FIX REACT HOOK ERROR)

    useEffect(() => {
        const disclaimerAccepted = localStorage.getItem('disclaimer_accepted');
        if (!disclaimerAccepted) setShowDisclaimer(true);

        const savedMsgs = localStorage.getItem('chat_history');
        if (savedMsgs) {
            setMessages(JSON.parse(savedMsgs));
        } else {
            setMessages([{ text: t.greeting, sender: 'bot' }]);
        }

        const tips = HEALTH_TIPS[language] || HEALTH_TIPS['en'];
        const randomTip = tips[Math.floor(Math.random() * tips.length)];
        setDailyTip(randomTip);

        // Load Profile
        const savedProfile = localStorage.getItem('user_profile');
        if (savedProfile) {
            setUserProfile(JSON.parse(savedProfile));
        } else {
            // If no profile, wait to see if hydration happens, then clear/open logic
            if (!isDoctor && user) {
                setTimeout(() => {
                    const freshProfile = localStorage.getItem('user_profile');
                    if (!freshProfile) setIsProfileOpen(true);
                }, 2000); // Wait 2s for Firestore hydration
            }
        }
    }, [isDoctor, user]);

    useEffect(() => {
        const tips = HEALTH_TIPS[language] || HEALTH_TIPS['en'];
        const randomTip = tips[Math.floor(Math.random() * tips.length)];
        setDailyTip(randomTip);
    }, [language]);

    useEffect(() => { localStorage.setItem('settings_language', language); }, [language]);
    useEffect(() => { localStorage.setItem('settings_muted', isMuted); }, [isMuted]);
    useEffect(() => { localStorage.setItem('settings_theme', theme); }, [theme]);
    useEffect(() => {
        if (messages.length > 0) localStorage.setItem('chat_history', JSON.stringify(messages));
    }, [messages]);

    const acceptDisclaimer = () => {
        localStorage.setItem('disclaimer_accepted', 'true');
        setShowDisclaimer(false);
    };

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = SpeechRecognition ? new SpeechRecognition() : null;

    useEffect(() => {
        if (recognition) recognition.lang = language === 'bn' ? 'bn-BD' : 'en-US';
    }, [language, recognition]);

    const startListening = async () => {
        if (!recognition) return alert("Browser does not support voice.");

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setAudioStream(stream);
        } catch (e) {
            console.error("Mic access denied", e);
        }

        setIsListening(true);
        recognition.start();
        recognition.onresult = (e) => {
            const transcript = e.results[0][0].transcript;

            // VOICE SOS TRIGGER (DISABLED BY USER REQUEST)
            /*
            if (/(help|emergency|bachao|banchao|sos|danger)/i.test(transcript)) {
                activateSOS();
                setMessages(prev => [...prev, { text: "SOS Voice Command Detected!", sender: 'bot' }]);
                setIsListening(false);
                return;
            }
            */

            setInputText(prev => prev + (prev ? ' ' : '') + transcript);
            setIsListening(false);
        };
        recognition.onend = () => setIsListening(false);
    };

    const handleFeedback = async (index, type) => {
        const newMessages = [...messages];
        newMessages[index].feedback = type;
        setMessages(newMessages);

        // Save to Firestore for Analytics
        try {
            await addDoc(collection(db, "ai_feedback"), {
                query: messages[index - 1]?.text || "Unknown",
                response: messages[index].text,
                rating: type,
                timestamp: new Date(),
                userId: user?.uid || "Anonymous"
            });
            // console.log("Feedback saved");
        } catch (e) { console.error("Feedback Error", e); }
    };

    const stopListening = () => {
        if (recognition) recognition.stop();
        setIsListening(false);
        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
            setAudioStream(null);
        }
    };

    const speakText = (text, index = null, langOverride = null) => {
        if (!window.speechSynthesis) return;

        if (index !== null && speakingIndex === index) {
            window.speechSynthesis.cancel();
            setSpeakingIndex(null);
            return;
        }

        window.speechSynthesis.cancel();
        if (isMuted) {
            setSpeakingIndex(null);
            return;
        }

        const targetLang = langOverride || language;
        const cleanText = text.replace(/<[^>]*>/g, '').replace(/Diagnosis:/g, '').replace(/URGENT_EMERGENCY/g, '');
        const utterance = new SpeechSynthesisUtterance(cleanText);

        if (targetLang === 'bn') {
            const voices = window.speechSynthesis.getVoices();
            const bnVoice = voices.find(v => v.lang === 'bn-BD') || voices.find(v => v.lang.includes('bn'));
            if (bnVoice) utterance.voice = bnVoice;
            utterance.rate = 0.9;
        }

        utterance.onend = () => setSpeakingIndex(null);
        utterance.onerror = () => setSpeakingIndex(null);

        if (index !== null) setSpeakingIndex(index);
        window.speechSynthesis.speak(utterance);
    };

    const toggleMute = () => {
        const newMute = !isMuted;
        setIsMuted(newMute);
        window.speechSynthesis.cancel();
        setSpeakingIndex(null);
    };

    const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text.replace(/<[^>]*>/g, ''));
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const handleDownloadReport = () => {
        const lastBotMsg = messages.slice().reverse().find(m => m.sender === 'bot');
        generateChatReport(messages, lastBotMsg ? lastBotMsg.text : "None", recommendedDoctors);
    };

    const toggleLanguage = () => {
        const newLang = language === 'en' ? 'bn' : 'en';
        setLanguage(newLang);
        const msg = newLang === 'bn' ? "ভাষা বাংলায় পরিবর্তন করা হয়েছে।" : "Language switched to English.";
        setMessages(prev => [...prev, { text: msg, sender: 'bot' }]);
        const newIndex = messages.length;
        speakText(msg, newIndex, newLang);
        playPopSound(isMuted);
    };

    const clearChat = () => {
        if (window.confirm("Clear all history?")) {
            localStorage.removeItem('chat_history');
            setMessages([{ text: t.greeting, sender: 'bot' }]);
            setRecommendedDoctors([]);
        }
    };

    const activateSOS = () => { setSosActive(true); setIsOpen(true); };

    const handleSymptomSelect = (symptom) => {
        setShowBodyMap(false);
        setInputText(symptom);
        handleSend(symptom);
    };

    const handleSaveProfile = (data) => {
        setUserProfile(data);
        localStorage.setItem('user_profile', JSON.stringify(data));
        setMessages(prev => [...prev, { text: `Nice to meet you, ${data.name}! I've updated my records.`, sender: 'bot' }]);
    };

    const handleSend = async (manualText = null) => {
        const textToSend = manualText || inputText;
        if (!textToSend.trim()) return;

        playPopSound(isMuted);
        const userMsgIndex = messages.length;
        setMessages(prev => [...prev, { text: textToSend, sender: 'user' }]);
        setInputText('');
        setLoading(true);
        setLoadingStatus(t.listening || "Initializing..."); // Start Phase via useEffect?? No, set distinct strings

        // Simulating phases (In real world, this would be streaming updates)
        const phases = [
            "🧠 Analyzing Symptoms...",
            "📚 Consulting Medical Database...",
            "✅ Formulating Response..."
        ];

        let phaseIdx = 0;
        setLoadingStatus(phases[0]);
        const phaseInterval = setInterval(() => {
            phaseIdx++;
            if (phaseIdx < phases.length) setLoadingStatus(phases[phaseIdx]);
        }, 1500); // Change phase every 1.5s while waiting

        setIsTypingDone(true);

        const historyPayload = messages.slice(-10).map(m => ({
            role: m.sender === 'bot' ? 'assistant' : 'user',
            content: m.text
        }));
        historyPayload.push({ role: 'user', content: textToSend });

        try {
            const aiResponse = await analyzeSymptoms(historyPayload, language, userProfile); // PASS PROFILE

            if (aiResponse) {
                if (aiResponse.includes("URGENT_EMERGENCY")) {
                    const cleanMsg = aiResponse.replace("URGENT_EMERGENCY", "").trim();
                    const alertMsg = `<b>${t.alertHdr}</b><br/>${cleanMsg}`;
                    const botMsgIndex = userMsgIndex + 1;

                    speakText(cleanMsg, botMsgIndex);
                    setMessages(prev => [...prev, { text: alertMsg, sender: 'bot' }]);
                    activateSOS();
                } else {
                    playPopSound(isMuted);
                    setIsTypingDone(false);

                    // 1. EXTRACT CARD
                    const cardMatch = aiResponse.match(/~CARD~([\s\S]*?)~END_CARD~/);
                    let cardData = null;
                    if (cardMatch) {
                        const cardText = cardMatch[1];
                        const condition = cardText.match(/Condition:\s*(.*)/)?.[1]?.trim();
                        const severity = cardText.match(/Severity:\s*(.*)/)?.[1]?.trim();
                        const action = cardText.match(/Action:\s*(.*)/)?.[1]?.trim();
                        if (condition) cardData = { condition, severity, action };
                    }

                    // 2. EXTRACT SUGGESTIONS
                    const chipsMatch = aiResponse.match(/~SUGGESTIONS~[\s\r\n]*(\[[^\]]*\])/);
                    if (chipsMatch) {
                        try {
                            const newChips = JSON.parse(chipsMatch[1]);
                            setDynamicChips(newChips); // Update Chips
                        } catch (e) { console.error("Failed to parse chips", e); }
                    }

                    // 3. CLEAN TEXT
                    let cleanMsg = aiResponse
                        .replace(/~CARD~[\s\S]*?~END_CARD~/, '')
                        .replace(/~SUGGESTIONS~[\s\S]*/, '')
                        .replace(/~STATS~/, '')
                        .replace(/~REMINDER~ .*/, '')
                        .replace(/~VITALS~ \{.*?\}/, '') // Remove Vitals Tag
                        .replace(/~SYMPTOMS~ \[.*?\]/, '') // Remove Symptoms Tag
                        .trim();

                    // 4. PARSE EXTRAS
                    let showStats = aiResponse.includes("~STATS~");

                    // -- VITALS SAVING --
                    const vitalsMatch = aiResponse.match(/~VITALS~(\{.*?\})/);
                    if (vitalsMatch) {
                        try {
                            const vitalsData = JSON.parse(vitalsMatch[1]);
                            // Save to Firestore 'patient_metrics'
                            await addDoc(collection(db, 'patient_metrics'), {
                                uid: auth.currentUser?.uid,
                                timestamp: new Date(),
                                type: 'vitals',
                                ...vitalsData
                            });
                            showStats = true; // Trigger stats refresh
                        } catch (e) {
                            console.error("Vitals Parse Error", e);
                        }
                    }

                    // -- SYMPTOMS SAVING --
                    const symptomsMatch = aiResponse.match(/~SYMPTOMS~(\[.*?\])/);
                    if (symptomsMatch) {
                        try {
                            const symptomsList = JSON.parse(symptomsMatch[1]);
                            await addDoc(collection(db, 'patient_metrics'), {
                                uid: auth.currentUser?.uid,
                                timestamp: new Date(),
                                type: 'symptoms',
                                symptoms: symptomsList
                            });
                        } catch (e) {
                            console.error("Symptoms Parse Error", e);
                        }
                    } const reminderMatch = aiResponse.match(/~REMINDER~ (.*?) \| (.*)/);
                    if (reminderMatch) {
                        const label = reminderMatch[2];
                        if (Notification.permission === "granted") {
                            new Notification(`Reminder set: ${label}`);
                        } else if (Notification.permission !== "denied") {
                            Notification.requestPermission();
                        }
                    }

                    const botMsgIndex = userMsgIndex + 1;
                    setMessages(prev => [...prev, {
                        text: cleanMsg,
                        sender: 'bot',
                        card: cardData,
                        stats: showStats,
                        feedback: null
                    }]);
                    speakText(cleanMsg, botMsgIndex);

                    const match = aiResponse.match(/Diagnosis:\s*([A-Za-z\s]+)/);
                    if (match) {
                        const specialist = match[1].split('.')[0].trim();
                        await fetchDoctors(specialist);
                    }
                }
            } else {
                setMessages(prev => [...prev, { text: t.errAI, sender: 'bot' }]);
            }
        } catch (error) {
            setMessages(prev => [...prev, { text: t.errGen, sender: 'bot' }]);
        } finally {
            clearInterval(phaseInterval);
            setLoading(false);
            setLoadingStatus('');
        }
    };

    const fetchDoctors = async (specialty) => {
        try {
            const docQuery = specialty.replace(/[^\w\s]/gi, '');
            const doctorsRef = collection(db, "doctors");
            const q = query(doctorsRef, where("specialty", "==", docQuery));
            const querySnapshot = await getDocs(q);
            const docs = [];
            querySnapshot.forEach((doc) => docs.push({ id: doc.id, ...doc.data() }));

            if (docs.length === 0) {
                const allDocs = await getDocs(collection(db, "doctors"));
                allDocs.forEach(doc => {
                    if (doc.data().specialty?.toLowerCase().includes(docQuery.toLowerCase())) {
                        docs.push({ id: doc.id, ...doc.data() });
                    }
                });
            }
            setRecommendedDoctors(docs);
        } catch (e) { console.error(e); }
    };

    const handleBook = (doctor) => setSelectedDoctor(doctor);
    const handleBookingConfirm = async (data) => {
        try {
            await addDoc(collection(db, "appointments"), {
                doctorId: data.doctor.id,
                doctorName: data.doctor.name,
                patientName: data.patientName,
                date: data.date,
                time: data.time,
                status: "pending",
                createdAt: new Date().toISOString()
            });
            const msg = `${t.bookSuccess} ${data.doctor.name}.`;
            const msgIndex = messages.length;
            setMessages(prev => [...prev, { text: msg, sender: 'bot' }]);
            speakText(msg, msgIndex);
            playPopSound(isMuted);
            setSelectedDoctor(null);
            setIsOpen(false);
            triggerConfetti(); // NEW: Confetti
        } catch (e) { console.error(e); }
    };

    const openMaps = (doc) => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${doc.name} ${doc.specialty} ${doc.location}`)}`, '_blank');
    useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sosActive, isTypingDone, showBodyMap]); // Scroll when body map toggles

    // Final Render Check: Hide if not logged in or is Doctor
    if (!user || isDoctor) return null;

    return (
        <>
            {!isOpen && (
                <button className={`chatbot-toggle-btn ${sosActive ? 'sos-pulse' : ''}`} onClick={() => setIsOpen(true)}>
                    {sosActive ? <AlertTriangle size={24} /> : <MessageCircle size={24} />}
                    <span className="chatbot-label">{sosActive ? (language === 'bn' ? "জরুরী" : "SOS ACTIVE") : (language === 'bn' ? "এআই সহকারী" : "AI Assistant")}</span>
                </button>
            )}

            {isOpen && (
                <div className={`chatbot-window ${sosActive ? 'sos-border' : ''}`} data-theme={theme}>
                    <div className={`chatbot-header ${sosActive ? 'sos-header' : ''}`}>
                        <div className="chatbot-header-title">
                            {sosActive ? <AlertTriangle size={20} /> : <Bot size={20} />}
                            <h3>{sosActive ? "EMERGENCY" : "PulseNote AI"}</h3>
                        </div>
                        <div className="chatbot-header-actions">
                            {!sosActive && (
                                <>
                                    <button onClick={toggleTheme} className="action-btn" title={theme === 'light' ? t.darkMode : t.lightMode}>{theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}</button>
                                    <button onClick={toggleMute} className="action-btn" title={isMuted ? t.unmute : t.mute}>{isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
                                    <button onClick={toggleLanguage} className="action-btn text-btn">{language === 'en' ? 'BN' : 'EN'}</button>
                                    <button onClick={toggleLanguage} className="action-btn text-btn">{language === 'en' ? 'BN' : 'EN'}</button>
                                    <button onClick={() => setIsDashboardOpen(true)} className="action-btn" title="Health Hub"><LayoutGrid size={18} /></button>
                                    <button onClick={() => setIsProfileOpen(true)} className="action-btn" title="Edit Profile"><User size={18} /></button>
                                    <button onClick={clearChat} className="action-btn"><Trash2 size={18} /></button>
                                    <button onClick={handleDownloadReport} className="action-btn"><FileText size={18} /></button>
                                </>
                            )}
                            <button onClick={() => setIsOpen(false)} className="action-btn"><X size={20} /></button>
                        </div>
                    </div>

                    <DashboardModal
                        isOpen={isDashboardOpen}
                        onClose={() => setIsDashboardOpen(false)}
                        userProfile={userProfile}
                        onBook={handleBook} // NEW: Pass booking handler
                    />

                    <div className="chatbot-messages">
                        {/* Health Tip Card */}
                        {!sosActive && messages.length < 3 && dailyTip && !showBodyMap && (
                            <div className="daily-tip-card">
                                <div className="tip-header">
                                    <Sparkles size={16} className="tip-icon" />
                                    <span>{t.tipTitle}</span>
                                </div>
                                <p>{dailyTip}</p>
                            </div>
                        )}

                        {showDisclaimer && (
                            <div className="disclaimer-overlay">
                                <div className="disclaimer-modal">
                                    <AlertTriangle size={40} className="disclaimer-icon" />
                                    <h3>{t.disclaimerTitle}</h3>
                                    <p>{t.disclaimerText}</p>
                                    <button onClick={acceptDisclaimer} className="disclaimer-btn">{t.understand}</button>
                                </div>
                            </div>
                        )}

                        {sosActive ? (
                            <div className="sos-screen">
                                <AlertTriangle size={64} className="sos-icon-large" />
                                <h2>{t.sosMsg}</h2>
                                <p>{t.sosSub}</p>
                                <div className="emergency-contacts">
                                    <a href="tel:999" className="sos-call-btn"><strong>999</strong><span>National Emergency</span></a>
                                    <a href="tel:16263" className="sos-call-btn"><strong>16263</strong><span>Health Hotline</span></a>
                                </div>
                                <button className="exit-sos-btn" onClick={() => setSosActive(false)}>{language === 'bn' ? "বন্ধ করুন" : "Exit SOS"}</button>
                            </div>
                        ) : (
                            <>
                                {/* MESSAGES */}
                                {messages.map((msg, idx) => (
                                    <div key={idx} className={`message ${msg.sender}`}>
                                        {msg.sender === 'bot' && <div className="avatar bot"><Bot size={16} /></div>}
                                        <div className="text-wrapper">
                                            <div className="text">
                                                {msg.sender === 'bot' && idx === messages.length - 1 && !isTypingDone ? (
                                                    <Typewriter text={msg.text} onComplete={() => setIsTypingDone(true)} />
                                                ) : (
                                                    <div className="markdown-body" dangerouslySetInnerHTML={{
                                                        __html: msg.text
                                                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')       // Bold
                                                            .replace(/\*(.*?)\*/g, '<em>$1</em>')                   // Italic
                                                            .replace(/^#\s+(.*$)/gim, '<h3>$1</h3>')                // Headers
                                                            .replace(/^\s*[-*]\s+(.*$)/gim, '<br/>• $1')            // Lists
                                                            .replace(/\n/g, '<br/>')                                // Newlines
                                                            .replace(/<br\/><br\/>/g, '<br/>')                      // Cleanup doubles
                                                    }} />
                                                )}
                                                {/* Render Medical Card if exists */}
                                                {msg.card && <MedicalCard data={msg.card} onAction={(actionText) => {
                                                    // Extract specialist from "Consult Dermatologist"
                                                    const match = actionText.match(/Consult\s+([A-Za-z\s]+)/i) || actionText.match(/See\s+([A-Za-z\s]+)/i);
                                                    const specialist = match ? match[1].trim() : "General Physician";
                                                    fetchDoctors(specialist);
                                                    setTimeout(() => {
                                                        document.querySelector('.doctor-list')?.scrollIntoView({ behavior: 'smooth' });
                                                    }, 500);
                                                }} />}

                                                {/* Render Health Stats */}
                                                {msg.stats && <HealthStats />}
                                            </div>

                                            {msg.sender === 'bot' && (
                                                <div className="bot-actions">
                                                    <div className="left-actions">
                                                        <button
                                                            className={`speak-btn ${speakingIndex === idx ? 'speaking-pulse' : ''}`}
                                                            onClick={() => speakText(msg.text, idx)}
                                                        >
                                                            {speakingIndex === idx ? <StopCircle size={12} color="#e53e3e" /> : <Volume2 size={12} />}
                                                        </button>
                                                        <button className="speak-btn" onClick={() => handleCopy(msg.text, idx)}>{copiedIndex === idx ? <CheckCircle size={12} color="green" /> : <Copy size={12} />}</button>
                                                    </div>
                                                    <div className="feedback-group">
                                                        <button className={msg.feedback === 'up' ? 'active' : ''} onClick={() => handleFeedback(idx, 'up')}><ThumbsUp size={12} /></button>
                                                        <button className={msg.feedback === 'down' ? 'active' : ''} onClick={() => handleFeedback(idx, 'down')}><ThumbsDown size={12} /></button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {msg.sender === 'user' && <div className="avatar user"><User size={16} /></div>}
                                    </div>
                                ))}

                                {/* LOADING INDICATOR */}
                                {/* LOADING INDICATOR */}
                                {loading && (
                                    <div className="message bot">
                                        <div className="avatar bot"><Bot size={16} className="animate-pulse" /></div>
                                        <div className="text-wrapper">
                                            <div className="text typing-indicator">
                                                <span>.</span><span>.</span><span>.</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* VISUAL BODY MAP TOGGLE */}
                                {showBodyMap && (
                                    <BodyMap onSymptomSelect={handleSymptomSelect} />
                                )}

                                {recommendedDoctors.length > 0 && (
                                    <div className="doctor-list">
                                        <p className="doc-list-title">{t.recTitle}</p>
                                        {recommendedDoctors.map(doc => (
                                            <div key={doc.id} className="doctor-card">
                                                <h4>{doc.name}</h4>
                                                <p>{language === 'bn' ? (SPECIALTY_MAP_BN[doc.specialty] || doc.specialty) : doc.specialty}</p>
                                                <div className="doc-actions-row">
                                                    <button onClick={() => openMaps(doc)} className="map-btn"><MapPin size={14} /></button>
                                                    <button onClick={() => handleBook(doc)} className="book-btn"><Calendar size={14} /> {t.bookBtn}</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </>
                        )}

                        {!sosActive && (
                            <div className="chatbot-input-area">
                                {messages.length < 3 && !showBodyMap ? (
                                    <div className="suggestion-chips">
                                        {(dynamicChips.length > 0 ? dynamicChips : t.chips).map((chip, i) => (
                                            <button key={i} className="chip" onClick={() => { setInputText(chip); handleSend(chip); }}>{chip}</button>
                                        ))}
                                    </div>
                                ) : (
                                    dynamicChips.length > 0 && (
                                        <div className="suggestion-chips">
                                            {dynamicChips.map((chip, i) => (
                                                <button key={i} className="chip" onClick={() => { setInputText(chip); handleSend(chip); }}>{chip}</button>
                                            ))}
                                        </div>
                                    )
                                )}

                                <div className="extra-actions">
                                    <button
                                        className={`icon-btn ${showBodyMap ? 'active' : ''}`}
                                        onClick={() => setShowBodyMap(!showBodyMap)}
                                        title={t.bodyMap}
                                    >
                                        <Accessibility size={18} />
                                    </button>
                                    <button className="sos-trigger-btn" onClick={activateSOS} title={t.sos}><AlertTriangle size={16} /></button>
                                </div>

                                <div className="chatbot-input">
                                    <button className={`mic-btn ${isListening ? 'listening' : ''}`} onClick={isListening ? stopListening : startListening}>
                                        {isListening ? <MicOff size={18} /> : <Mic size={18} />}
                                    </button>
                                    <input type="text" placeholder={isListening ? t.listening : t.placeholder} value={inputText} onChange={(e) => setInputText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSend()} disabled={isListening} />
                                    <button onClick={() => handleSend()} disabled={loading || (!inputText.trim() && !isListening)} className="send-btn"><Send size={18} /></button>
                                </div>
                                {isListening && audioStream && (
                                    <div className="waveform-container">
                                        <VoiceWaveform stream={audioStream} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {selectedDoctor && <BookingModal doctor={selectedDoctor} onClose={() => setSelectedDoctor(null)} onBook={handleBookingConfirm} />}
                    <UserProfileModal
                        isOpen={isProfileOpen}
                        onClose={() => setIsProfileOpen(false)}
                        onSave={handleSaveProfile}
                        initialData={userProfile}
                    />
                    <DashboardModal
                        isOpen={isDashboardOpen}
                        onClose={() => setIsDashboardOpen(false)}
                        userProfile={userProfile}
                    />
                </div >
            )}
        </>
    );
};
export default ChatBot;
